import React, { forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactDatePicker from 'react-datepicker';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './Calendar.module.scss';

const toDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const toIsoDay = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const FieldButton = forwardRef(function FieldButton(
  { value, onClick, placeholder, disabled, error, hasValue, onClear },
  ref
) {
  const classes = [styles.field, error ? styles.fieldError : ''].filter(Boolean).join(' ');
  return (
    <div className={styles.fieldWrap}>
      <span className={styles.leftIcon}>
        <CalendarIcon size={14} />
      </span>
      <button
        ref={ref}
        type="button"
        className={classes}
        onClick={onClick}
        disabled={disabled}
      >
        {value ? value : <span style={{ color: 'var(--color-text-muted)' }}>{placeholder}</span>}
      </button>
      {hasValue && !disabled && (
        <button
          type="button"
          className={styles.clearBtn}
          aria-label="Clear date"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClear?.();
          }}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
});

const Calendar = ({
  mode,
  label,
  value,
  onChange,
  placeholder,
  error,
  helperText,
  disabled,
  minDate,
  maxDate,
  id,
  className,
}) => {
  const wrapperClasses = [styles.wrapper, className || ''].filter(Boolean).join(' ');

  const isRange = mode === 'range';

  const startDate = useMemo(
    () => (isRange ? toDate(Array.isArray(value) ? value[0] : null) : toDate(value)),
    [isRange, value]
  );
  const endDate = useMemo(
    () => (isRange ? toDate(Array.isArray(value) ? value[1] : null) : null),
    [isRange, value]
  );

  const fmt = (d) =>
    d
      ? d.toLocaleDateString(undefined, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '';

  const displayValue = isRange
    ? startDate || endDate
      ? `${fmt(startDate) || '…'} → ${fmt(endDate) || '…'}`
      : ''
    : fmt(startDate);

  const handleChange = (next) => {
    if (isRange) {
      const [s, e] = Array.isArray(next) ? next : [null, null];
      onChange?.([toIsoDay(s), toIsoDay(e)]);
    } else {
      onChange?.(toIsoDay(next));
    }
  };

  const handleClear = () => {
    if (isRange) onChange?.(['', '']);
    else onChange?.('');
  };

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}
      <ReactDatePicker
        id={id}
        selected={startDate}
        startDate={isRange ? startDate : undefined}
        endDate={isRange ? endDate : undefined}
        selectsRange={isRange || undefined}
        onChange={handleChange}
        disabled={disabled}
        minDate={toDate(minDate)}
        maxDate={toDate(maxDate)}
        dateFormat="dd MMM yyyy"
        shouldCloseOnSelect={!isRange}
        popperPlacement="bottom-start"
        customInput={
          <FieldButton
            placeholder={placeholder}
            disabled={disabled}
            error={!!error}
            hasValue={!!(startDate || endDate)}
            onClear={handleClear}
            value={displayValue}
          />
        }
      />
      {error && <span className={styles.error}>{error}</span>}
      {!error && helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  );
};

Calendar.propTypes = {
  mode: PropTypes.oneOf(['single', 'range']),
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])),
  ]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  id: PropTypes.string,
  className: PropTypes.string,
};

Calendar.defaultProps = {
  mode: 'single',
  label: '',
  value: '',
  onChange: () => {},
  placeholder: 'Select date',
  error: '',
  helperText: '',
  disabled: false,
  minDate: undefined,
  maxDate: undefined,
  id: undefined,
  className: '',
};

export default Calendar;
